import { z } from 'zod';

// =============================================================================
// Call Evaluation — post-call LLM evaluation of an outbound voice call.
// =============================================================================

/**
 * The call's headline triage signal. Derived server-side from gate verdicts,
 * the misinformed flag, the category scores and whether a callback exists —
 * never authored by the model.
 */
export const evaluationStatusSchema = z.enum(['all_clear', 'follow_up', 'needs_you']);
export type EvaluationStatus = z.infer<typeof evaluationStatusSchema>;

export const evalMethodSchema = z.enum(['llm', 'fallback']);
export type EvalMethod = z.infer<typeof evalMethodSchema>;

export const callbackSuppressedReasonSchema = z.enum(['declined', 'wrong_number', 'booked']);
export type CallbackSuppressedReason = z.infer<typeof callbackSuppressedReasonSchema>;

/** The LLM evaluator's own callback verdict, stored independently of the codebase decision. */
export const callbackRecommendationSchema = z.enum([
  'recommend',
  'patient_declined',
  'wrong_number',
  'booked',
]);
export type CallbackRecommendation = z.infer<typeof callbackRecommendationSchema>;

/** Internal letter grade. Never customer-visible. */
export const callGradeSchema = z.enum(['A', 'B', 'C', 'D', 'F']);
export type CallGrade = z.infer<typeof callGradeSchema>;

/**
 * The five deterministic safety gates. Internal-only.
 *
 * MUST STAY IN STEP WITH `GATE` in apps/api/src/core/call-evaluation/types.ts,
 * which is the definition; this is the wire contract the payload is validated
 * against. A gate added there and not here does not fail at the gate — it fails
 * when the finished evaluation is serialized, one layer from where the change
 * was made.
 */
export const gateNameSchema = z.enum([
  'G-DOBGATE',
  'G-UNDISCLOSED',
  'G-STRAND',
  'G-WRONGSLOT',
  'G-CONFAB',
]);
export type GateName = z.infer<typeof gateNameSchema>;

export const gateResultSchema = z.object({
  gate: gateNameSchema,
  passed: z.boolean(),
  detail: z.string().nullable(),
  evidenceSeq: z.number().nullable(),
});
export type GateResult = z.infer<typeof gateResultSchema>;

/** The four judged categories. */
export const categoryNameSchema = z.enum(['safety', 'accuracy', 'playbook', 'experience']);
export type CategoryName = z.infer<typeof categoryNameSchema>;

/**
 * What the practice sees for one category — a check or a nudge, never the
 * number. Derived server-side from the score, so a verdict and a score can
 * never disagree.
 */
export const checkmarkSchema = z.enum(['clear', 'attention']);
export type Checkmark = z.infer<typeof checkmarkSchema>;

/**
 * One category as the CLIENT receives it. No score, no findings — those are
 * added alongside for system admins only (see `adminCategoryScoreSchema`).
 *
 * `clientNote` is empty on a clean category, which is the common case and the
 * reason a good call reads as almost nothing.
 */
export const categoryViewSchema = z.object({
  checkmark: checkmarkSchema,
  clientNote: z.string(),
});
export type CategoryView = z.infer<typeof categoryViewSchema>;

/** One category as a SYSTEM ADMIN receives it: the client view plus the internals. */
export const adminCategoryScoreSchema = categoryViewSchema.extend({
  score: z.number(),
  findings: z.array(z.string()),
});
export type AdminCategoryScore = z.infer<typeof adminCategoryScoreSchema>;

export const categoryViewsSchema = z.record(categoryNameSchema, categoryViewSchema);
export type CategoryViews = z.infer<typeof categoryViewsSchema>;

export const adminCategoryScoresSchema = z.record(categoryNameSchema, adminCategoryScoreSchema);
export type AdminCategoryScores = z.infer<typeof adminCategoryScoresSchema>;

/** Which backbone steps ran. Computed from the message stream, never judged. */
export const playbookStepsSchema = z.object({
  expected: z.array(z.string()),
  executed: z.array(z.string()),
  missed: z.array(z.string()),
});
export type PlaybookSteps = z.infer<typeof playbookStepsSchema>;

/** Structured specifics behind the customer-facing outcome label. */
export const outcomeDetailSchema = z.object({
  startTime: z.string(),
  providerName: z.string().nullable(),
  locationName: z.string().nullable(),
  /**
   * IANA zone the label was rendered in — format `startTime` in THIS zone to
   * match the headline. `.default(null)` because rows persisted before the
   * field existed have no key, and this schema `.parse()`s them on every read.
   */
  locationTimezone: z.string().nullable().default(null),
});
export type OutcomeDetail = z.infer<typeof outcomeDetailSchema>;

/**
 * Read shape for a call evaluation, split by audience.
 *
 * **Client fields** (always present) tell the practice what happened and
 * whether anyone needs to do something: `status`, `headline`, `officeNote`, the
 * deterministic `outcomeLabel`, and `categories` — a checkmark plus an
 * optional one-line note per category. No score ever crosses this line.
 *
 * **Internal fields** are `.optional()` because the API OMITS them entirely for
 * non-system-admin viewers: the raw category scores and findings, the grade,
 * the gate verdicts, the playbook steps and the improvement list. This is not
 * cosmetic — a customer-facing report card on the AI invites contractual
 * argument instead of useful action, so the grade is stored for our own triage
 * and never shown. `undefined` here means "you are not a staff viewer", NOT
 * "the call had no grade"; the exception is `grade` itself, genuinely null on
 * a fallback evaluation.
 *
 * **Nearly every field is nullable, and that is not laziness.** A `fallback`
 * evaluation — the LLM call failed, timed out, or was disabled — legitimately
 * carries no categories, no grade and no notes, only the deterministic
 * decision. The route `.parse()`s on every read, so a required `categoryScores`
 * or `grade` would throw on exactly the rows produced when the evaluator is
 * already degraded.
 */
export const callEvaluationResponseSchema = z.object({
  id: z.string().uuid(),
  interactionId: z.string().uuid(),
  patientId: z.string().uuid().nullable(),

  // ─── Client-facing (every viewer) ───
  /** Headline triage signal. Derived server-side, never model-authored. */
  status: evaluationStatusSchema.nullable().default(null),
  /** One line on how the call fared, in business language. */
  headline: z.string().nullable().default(null),
  /** Action the office owes, and/or the notable good thing. Null when neither. */
  officeNote: z.string().nullable().default(null),
  /** Per-category checkmark + optional note. No scores. */
  categories: categoryViewsSchema.nullable().default(null),
  /** Plain-language outcome, rendered deterministically — never model-authored. */
  outcomeLabel: z.string().nullable(),
  outcomeDetail: outcomeDetailSchema.nullable(),
  recommendCallback: z.boolean(),
  callbackTaskId: z.string().uuid().nullable(),
  suppressedReason: callbackSuppressedReasonSchema.nullable(),
  outcomeCategory: z.string().nullable(),
  /**
   * Whether an appointment was created on THIS call — proven from
   * `scheduled_appointments.interaction_id`, the same fact the G-CONFAB gate
   * judges on.
   *
   * DERIVED at read time, not stored on the row: the appointment record is
   * already the source of truth, so a snapshot could only go stale, and a
   * column would read null for every evaluation written before it existed.
   * Cancelling or rescheduling later does not flip it — the lookup ignores
   * appointment `status`, because a booking that was cancelled next week still
   * happened on the call.
   *
   * Read this, not `outcomeCategory`, to answer "was it booked". The two
   * disagree: the disposition recorded `engaged_no_booking` for 6 of 6
   * production v2 calls that had committed a real appointment (ADR-188), and
   * inbound calls never write a disposition at all.
   */
  bookedOnThisCall: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),

  // ─── Internal only: omitted (undefined) for non-admin viewers ───
  /** System-admin only. The client view plus the raw score and findings. */
  categoryScores: adminCategoryScoresSchema.nullable().optional(),
  /** System-admin only. Which backbone steps ran. Computed, not judged. */
  playbookSteps: playbookStepsSchema.nullable().optional(),
  /** System-admin only. Engineering next-actions; empty on a clean call. */
  improvements: z.array(z.string()).nullable().optional(),
  /** System-admin only. `null` on a fallback — see the schema doc. */
  grade: callGradeSchema.nullable().optional(),
  /** System-admin only. Weighted 1–5 before the gate cap. */
  overallScore: z.number().nullable().optional(),
  /** System-admin only. Deterministic safety-gate verdicts. */
  gateResults: z.array(gateResultSchema).nullable().optional(),
  /** System-admin only. What the judge reported the agent SAID about a booking. */
  agentClaimedBooking: z.boolean().optional(),
  /** System-admin only. The LLM's own callback verdict. */
  llmCallbackRecommendation: callbackRecommendationSchema.nullable().optional(),
  /** System-admin only. */
  patientMisinformed: z.boolean().optional(),
  /** System-admin only. */
  model: z.string().optional(),
  /** System-admin only. */
  method: evalMethodSchema.optional(),
  /** System-admin only. */
  confidence: z.number().nullable().optional(),
});

export type CallEvaluationResponse = z.infer<typeof callEvaluationResponseSchema>;
